import { useState, useEffect, useCallback, useMemo } from 'react';
import { itemsAPI } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { formatNumber } from '../lib/utils';
import { Card, CardContent } from '../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Input } from '../components/ui/input';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../components/ui/tooltip';
import ZohoSyncStamp from '../components/layout/ZohoSyncStamp';
import { toast } from 'sonner';
import {
  ShieldAlert, AlertTriangle, CheckCircle, Loader2, Bell,
  ArrowUpDown, ArrowUp, ArrowDown, Search, Package, Clock,
  TrendingUp, TrendingDown, BarChart3
} from 'lucide-react';

export default function RiskDashboardPage() {
  const [alerts, setAlerts] = useState({});
  const [summary, setSummary] = useState(null);
  const [analytics, setAnalytics] = useState({});
  const [agingData, setAgingData] = useState({});
  const [loading, setLoading] = useState(true);
  const [riskFilter, setRiskFilter] = useState('all_risk');
  const [search, setSearch] = useState('');
  const [sortField, setSortField] = useState('sellable_gap');
  const [sortOrder, setSortOrder] = useState('asc');
  const { canAccess } = useAuth();

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [riskRes, analyticsRes, agingRes] = await Promise.all([
        itemsAPI.getRiskAlerts(),
        itemsAPI.getPriceAnalytics(14),
        itemsAPI.getAgingSummary(),
      ]);
      setAlerts(riskRes.data.alerts || {});
      setSummary(riskRes.data.summary || null);
      setAnalytics(analyticsRes.data.analytics || {});
      setAgingData(agingRes.data.aging || {});
    } catch (e) {
      toast.error('Failed to load risk data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSort = (field) => {
    if (sortField === field) setSortOrder(o => o === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortOrder('asc'); }
  };

  const items = useMemo(() => {
    let list = Object.entries(alerts).map(([sku, data]) => ({ sku, ...data }));

    // Filter by risk level
    if (riskFilter === 'critical') list = list.filter(i => i.risk === 'CRITICAL');
    else if (riskFilter === 'at_risk') list = list.filter(i => i.risk === 'AT_RISK');
    else if (riskFilter === 'all_risk') list = list.filter(i => i.risk !== 'SAFE');
    // 'all' shows everything

    // Search
    if (search) {
      const q = search.toLowerCase();
      list = list.filter(i => i.sku.toLowerCase().includes(q) || (i.item_name || '').toLowerCase().includes(q));
    }

    // Sort
    const dir = sortOrder === 'asc' ? 1 : -1;
    list.sort((a, b) => {
      let va = a[sortField], vb = b[sortField];
      if (typeof va === 'string') return va.localeCompare(vb) * dir;
      return ((va || 0) - (vb || 0)) * dir;
    });

    return list;
  }, [alerts, riskFilter, search, sortField, sortOrder]);

  // Compute severity distribution for the bar chart
  const severityBuckets = useMemo(() => {
    const all = Object.values(alerts).filter(a => a.risk !== 'SAFE');
    if (!all.length) return [];
    const sorted = [...all].sort((a, b) => a.sellable_gap - b.sellable_gap);
    const worst = Math.abs(sorted[0]?.sellable_gap || 1);
    return sorted.slice(0, 15).map(a => ({
      sku: a.sku || '',
      name: a.item_name || a.sku || '',
      gap: a.sellable_gap,
      pct: Math.min(100, (Math.abs(a.sellable_gap) / worst) * 100),
      risk: a.risk,
    }));
  }, [alerts]);

  const SortIcon = ({ field }) => {
    if (sortField !== field) return <ArrowUpDown className="h-3 w-3 ml-1 opacity-40" />;
    return sortOrder === 'asc' ? <ArrowUp className="h-3 w-3 ml-1 text-primary" /> : <ArrowDown className="h-3 w-3 ml-1 text-primary" />;
  };
  const SortHeader = ({ field, children, className = '' }) => (
    <TableHead className={`font-medium text-xs uppercase cursor-pointer hover:bg-muted/80 select-none ${className}`} onClick={() => handleSort(field)}>
      <div className="flex items-center">{children}<SortIcon field={field} /></div>
    </TableHead>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20" data-testid="risk-dashboard-loading">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="risk-dashboard-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
            <ShieldAlert className="h-6 w-6 text-red-600" />
            Risk Dashboard
          </h1>
          <p className="text-muted-foreground text-sm">Sellable stock risk overview — prioritize buying decisions</p>
          <ZohoSyncStamp className="mt-1" />
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={fetchData} className="rounded-sm" data-testid="refresh-risk-btn">
            <BarChart3 className="h-4 w-4 mr-2" />Refresh
          </Button>
          {canAccess('RECONCILE', 'VIEW') && (
            <Button disabled title="Stock risk notifications are disabled while stock sync is restricted" className="rounded-sm opacity-50 cursor-not-allowed" data-testid="alert-pms-btn">
              <Bell className="h-4 w-4 mr-2" />Alert PMs (Disabled)
            </Button>
          )}
        </div>
      </div>

      {/* Stock sync restriction notice */}
      <div className="flex items-center gap-2 rounded-sm border border-amber-200 bg-amber-50/60 px-4 py-3 text-amber-800 text-sm" data-testid="risk-dashboard-not-in-use-banner">
        <AlertTriangle className="h-4 w-4 flex-shrink-0" />
        <span>
          <strong>Not in use:</strong> Stock-based risk analysis is restricted while per-item stock detail sync is
          disabled (Zoho API quota protection). Figures below may be unreliable — treat as informational only.
        </span>
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="rounded-sm border-red-200 bg-red-50/30" data-testid="critical-card">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 text-red-700 mb-1">
                <ShieldAlert className="h-4 w-4" />
                <span className="text-xs font-medium uppercase">Critical</span>
              </div>
              <p className="text-3xl font-bold font-mono text-red-700">{summary.critical}</p>
              <p className="text-xs text-red-600/70 mt-1">Severe stock shortfall</p>
            </CardContent>
          </Card>
          <Card className="rounded-sm border-amber-200 bg-amber-50/30" data-testid="at-risk-card">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 text-amber-700 mb-1">
                <AlertTriangle className="h-4 w-4" />
                <span className="text-xs font-medium uppercase">At Risk</span>
              </div>
              <p className="text-3xl font-bold font-mono text-amber-700">{summary.at_risk}</p>
              <p className="text-xs text-amber-600/70 mt-1">Demand exceeds supply</p>
            </CardContent>
          </Card>
          <Card className="rounded-sm border-emerald-200 bg-emerald-50/30" data-testid="safe-card">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 text-emerald-700 mb-1">
                <CheckCircle className="h-4 w-4" />
                <span className="text-xs font-medium uppercase">Safe</span>
              </div>
              <p className="text-3xl font-bold font-mono text-emerald-700">{summary.safe}</p>
              <p className="text-xs text-emerald-600/70 mt-1">Sufficient stock</p>
            </CardContent>
          </Card>
          <Card className="rounded-sm" data-testid="total-demand-card">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 text-muted-foreground mb-1">
                <Package className="h-4 w-4" />
                <span className="text-xs font-medium uppercase">Total with Demand</span>
              </div>
              <p className="text-3xl font-bold font-mono">{summary.total_skus_with_demand}</p>
              <p className="text-xs text-muted-foreground mt-1">SKUs with open orders</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Shortfall Chart */}
      {severityBuckets.length > 0 && (
        <Card className="rounded-sm" data-testid="shortfall-chart">
          <CardContent className="p-4">
            <p className="text-sm font-semibold mb-3">Top Shortfalls (units)</p>
            <div className="space-y-1.5">
              {severityBuckets.map((item, i) => (
                <div key={i} className="flex items-center gap-2 group" data-testid={`chart-bar-${i}`}>
                  <span className="text-xs font-mono w-[140px] truncate text-muted-foreground group-hover:text-foreground transition-colors" title={item.name}>
                    {item.sku}
                  </span>
                  <div className="flex-1 h-5 bg-muted/30 rounded-sm overflow-hidden relative">
                    <div
                      className={`h-full rounded-sm transition-all ${item.risk === 'CRITICAL' ? 'bg-red-500' : 'bg-amber-400'}`}
                      style={{ width: `${item.pct}%` }}
                    />
                    <span className="absolute right-2 top-0 h-full flex items-center text-[10px] font-mono font-medium text-foreground/70">
                      {formatNumber(Math.abs(item.gap))}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Filters */}
      <Card className="rounded-sm">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input placeholder="Search SKU or name..." value={search} onChange={e => setSearch(e.target.value)} className="pl-10 rounded-sm" data-testid="risk-search" />
            </div>
            <Select value={riskFilter} onValueChange={setRiskFilter}>
              <SelectTrigger className="w-44 rounded-sm" data-testid="risk-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all_risk">At Risk + Critical</SelectItem>
                <SelectItem value="critical">Critical Only</SelectItem>
                <SelectItem value="at_risk">At Risk Only</SelectItem>
                <SelectItem value="all">All (incl. Safe)</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Risk Table */}
      <Card className="rounded-sm">
        <CardContent className="p-0 overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-muted/50">
                <TableHead className="font-medium text-xs uppercase w-8">#</TableHead>
                <SortHeader field="sku">SKU</SortHeader>
                <SortHeader field="item_name" className="min-w-[180px]">Name</SortHeader>
                <TableHead className="font-medium text-xs uppercase text-center">Risk</TableHead>
                <SortHeader field="available_for_sale" className="text-right">Available</SortHeader>
                <SortHeader field="open_demand" className="text-right">Demand</SortHeader>
                <SortHeader field="sellable_gap" className="text-right">Gap</SortHeader>
                <SortHeader field="monthly_avg_sales" className="text-right">Avg/Mo</SortHeader>
                <TableHead className="font-medium text-xs uppercase text-right">Avg Buy</TableHead>
                <TableHead className="font-medium text-xs uppercase text-right">Avg Sell</TableHead>
                <TableHead className="font-medium text-xs uppercase text-right">Spread</TableHead>
                <TableHead className="font-medium text-xs uppercase text-center">Age</TableHead>
                <SortHeader field="assigned_to_name">PM</SortHeader>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={13} className="text-center py-12">
                    <CheckCircle className="h-12 w-12 mx-auto text-emerald-400 mb-2" />
                    <p className="text-muted-foreground">No risk items found</p>
                  </TableCell>
                </TableRow>
              ) : (
                items.map((item, idx) => {
                  const a = analytics[item.sku] || {};
                  const age = agingData[item.sku];
                  const gapPct = item.open_demand > 0 ? ((item.sellable_gap / item.open_demand) * 100).toFixed(0) : 0;
                  return (
                    <TableRow key={item.sku} data-testid={`risk-row-${item.sku}`} className="table-row-hover">
                      <TableCell className="text-xs text-muted-foreground font-mono">{idx + 1}</TableCell>
                      <TableCell className="font-mono text-xs font-medium text-primary">{item.sku}</TableCell>
                      <TableCell className="text-sm max-w-[200px]"><div className="truncate">{item.item_name || item.sku}</div></TableCell>
                      <TableCell className="text-center">
                        {item.risk === 'CRITICAL' ? (
                          <Badge variant="destructive" className="rounded-sm text-xs" data-testid={`risk-badge-${item.sku}`}>
                            <ShieldAlert className="h-3 w-3 mr-1" />Critical
                          </Badge>
                        ) : item.risk === 'AT_RISK' ? (
                          <Badge variant="outline" className="rounded-sm text-xs bg-amber-50 text-amber-700 border-amber-200" data-testid={`risk-badge-${item.sku}`}>
                            <AlertTriangle className="h-3 w-3 mr-1" />At Risk
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="rounded-sm text-xs bg-emerald-50 text-emerald-700" data-testid={`risk-badge-${item.sku}`}>
                            <CheckCircle className="h-3 w-3 mr-1" />Safe
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-right font-mono text-sm">{formatNumber(item.available_for_sale)}</TableCell>
                      <TableCell className="text-right font-mono text-sm text-blue-600">{formatNumber(item.open_demand)}</TableCell>
                      <TableCell className="text-right">
                        <TooltipProvider delayDuration={200}><Tooltip><TooltipTrigger asChild>
                          <span className={`font-mono text-sm font-medium ${item.sellable_gap >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                            {item.sellable_gap >= 0 ? '+' : ''}{formatNumber(item.sellable_gap)}
                          </span>
                        </TooltipTrigger>
                        <TooltipContent className="text-xs">
                          <p>Gap: {formatNumber(item.sellable_gap)} units ({gapPct}%)</p>
                          <p>Available: {formatNumber(item.available_for_sale)}</p>
                          <p>Open demand: {formatNumber(item.open_demand)}</p>
                          <p>Orders: {item.order_count}</p>
                        </TooltipContent></Tooltip></TooltipProvider>
                      </TableCell>
                      <TableCell className="text-right font-mono text-sm text-muted-foreground">
                        {item.monthly_avg_sales > 0 ? formatNumber(Math.round(item.monthly_avg_sales)) : '—'}
                      </TableCell>
                      <TableCell className="text-right font-mono text-sm">
                        {a.avg_buy != null ? `$${a.avg_buy.toFixed(2)}` : '—'}
                      </TableCell>
                      <TableCell className="text-right font-mono text-sm">
                        {a.avg_sell != null ? `$${a.avg_sell.toFixed(2)}` : '—'}
                      </TableCell>
                      <TableCell className="text-right">
                        {a.spread != null ? (
                          <span className={`font-mono text-sm ${a.spread >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                            {a.spread >= 0 ? '+' : ''}{a.spread.toFixed(2)}
                          </span>
                        ) : <span className="text-muted-foreground">—</span>}
                      </TableCell>
                      <TableCell className="text-center">
                        {age ? (
                          <span className={`text-xs font-mono ${age.is_old ? 'text-amber-700' : 'text-muted-foreground'}`}>
                            {age.is_old && <Clock className="h-3 w-3 inline mr-0.5" />}
                            {age.avg_age_days}d
                          </span>
                        ) : <span className="text-muted-foreground text-xs">—</span>}
                      </TableCell>
                      <TableCell className="text-sm">
                        {item.assigned_to_name || <span className="text-muted-foreground text-xs">—</span>}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <p className="text-xs text-muted-foreground text-center">
        Showing {items.length} items | Risk computed from confirmed Sales Orders vs available stock
      </p>
    </div>
  );
}
